import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Navbar from '../components/Navbar';

function Groups() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all'); // all, my-groups, suggested, invites
  const [groups, setGroups] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userIdentities, setUserIdentities] = useState([]);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    locationCity: '',
    locationState: '',
    targetIdentityId: ''
  });

  useEffect(() => {
    fetchGroups();
    fetchUserIdentities();
  }, [activeTab]);

  // Fetch invite count on mount so the badge shows immediately
  useEffect(() => {
    fetchInviteCount();
  }, []);

  const fetchUserIdentities = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const response = await axios.get(
        `/api/identities/me`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const identities = response.data.identities || [];
      setUserIdentities(identities);
      
      // Set default target identity to first one
      if (identities.length > 0) {
        setNewGroup(prev => ({ ...prev, targetIdentityId: identities[0].identity_id }));
      }
    } catch (error) {
      console.error('Error fetching user identities:', error);
      if (error.response?.status === 401) {
        setMessage('Session expired. Please refresh the page and try again.');
      }
    }
  };

  const fetchInviteCount = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/groups/invites/received', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(response.data.invites || []);
    } catch (error) {
      // Silently fail - invite count is not critical
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (activeTab === 'invites') {
        const response = await axios.get('/api/groups/invites/received', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvites(response.data.invites);
      } else {
        const filter = activeTab === 'all' ? '' : activeTab;
        const response = await axios.get(`/api/groups${filter ? `?filter=${filter}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGroups(response.data.groups);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setMessage('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post('/api/groups', newGroup, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Group created successfully!');
      setShowCreateModal(false);
      setNewGroup({
        name: '',
        description: '',
        locationCity: '',
        locationState: '',
        targetIdentityId: userIdentities.length > 0 ? userIdentities[0].identity_id : ''
      });
      fetchGroups();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to create group');
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to leave this group?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`/api/groups/${groupId}/leave`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('Left group successfully');
      fetchGroups();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to leave group');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/groups/invites/${inviteId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Invite accepted! Group added to "My Groups"');
      fetchGroups();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/groups/invites/${inviteId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('Invite declined');
      fetchGroups();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to decline invite');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
              <h2>Your groups</h2>
              <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                🔒 All groups are private. Invite-only for your safety.
              </p>
            </div>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Start
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setActiveTab('all')}
              className={activeTab === 'all' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              All
            </button>
            <button 
              onClick={() => setActiveTab('my-groups')}
              className={activeTab === 'my-groups' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Mine
            </button>
            <button 
              onClick={() => setActiveTab('suggested')}
              className={activeTab === 'suggested' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              For you
            </button>
            <button 
              onClick={() => setActiveTab('invites')}
              className={activeTab === 'invites' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Invites {invites.length > 0 && `(${invites.length})`}
            </button>
          </div>
        </div>

        {message && (
          <div className="card" style={{
            backgroundColor: message.includes('✅') || message.includes('success') ? '#d4edda' : '#f8d7da',
            color: message.includes('✅') || message.includes('success') ? '#155724' : '#721c24'
          }}>
            {message}
          </div>
        )}

        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>Loading...</div>
        ) : activeTab === 'invites' ? (
          invites.length === 0 ? (
            <div className="card">
              <p>No invites yet!</p>
            </div>
          ) : (
            invites.map((invite) => (
              <div key={invite.invite_id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <h3>{invite.group_name}</h3>
                    {invite.privacy === 'private' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        marginLeft: '8px'
                      }}>
                        🔒 Private
                      </span>
                    )}
                    
                    {invite.group_description && (
                      <p style={{ marginTop: '8px', color: '#666' }}>
                        {invite.group_description}
                      </p>
                    )}
                    
                    {invite.target_identity && (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{ 
                          padding: '2px 8px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {invite.target_identity.icon} {invite.target_identity.category_display}
                        </span>
                      </div>
                    )}
                    
                    <div style={{ marginTop: '12px', fontSize: '14px', color: '#999' }}>
                      <p>
                        Invited by <strong>{invite.inviter_first_name} {invite.inviter_last_name}</strong>
                      </p>
                      <span>👥 {invite.member_count} member{invite.member_count !== 1 ? 's' : ''}</span>
                      {invite.location_city && (
                        <span style={{ marginLeft: '16px' }}>
                          📍 {invite.location_city}, {invite.location_state}
                        </span>
                      )}
                      <p style={{ marginTop: '4px' }}>
                        Invited {new Date(invite.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px', marginLeft: '16px' }}>
                    <button 
                      onClick={() => handleAcceptInvite(invite.invite_id)}
                      className="btn btn-primary"
                    >
                      Join
                    </button>
                    <button 
                      onClick={() => handleDeclineInvite(invite.invite_id)}
                      className="btn btn-secondary"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))
          )
        ) : groups.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px', opacity: 0.5 }} aria-hidden="true">👥</div>
            <p style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>No groups yet</p>
            {activeTab === 'my-groups' && (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                Start a group or wait for an invite from a connection.
              </p>
            )}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.group_id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h3>{group.name}</h3>
                    {group.privacy === 'private' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        🔒 Private
                      </span>
                    )}
                    {group.my_role === 'admin' && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Admin
                      </span>
                    )}
                  </div>
                  
                  {group.description && (
                    <p style={{ marginTop: '8px', color: '#666' }}>
                      {group.description}
                    </p>
                  )}
                  
                  {group.target_identity && (
                    <div style={{ marginTop: '8px' }}>
                      <span style={{ 
                        padding: '2px 8px',
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {group.target_identity.icon} {group.target_identity.category_display}
                      </span>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '12px', fontSize: '14px', color: '#999' }}>
                    <span>👥 {group.member_count} member{group.member_count !== 1 ? 's' : ''}</span>
                    {group.location_city && (
                      <span style={{ marginLeft: '16px' }}>
                        📍 {group.location_city}, {group.location_state}
                      </span>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                  {group.is_member ? (
                    <>
                      <button 
                        onClick={() => navigate(`/groups/${group.group_id}`)}
                        className="btn btn-primary"
                      >
                        View
                      </button>
                      {group.my_role !== 'admin' && (
                        <button 
                          onClick={() => handleLeaveGroup(group.group_id)}
                          className="btn btn-secondary"
                        >
                          Leave
                        </button>
                      )}
                    </>
                  ) : (
                    <span style={{
                      padding: '8px 16px',
                      backgroundColor: '#f8f9fa',
                      color: '#6c757d',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      🔒 Invite only
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ 
            maxWidth: '500px', 
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2>Start a group</h2>
            
            <form onSubmit={handleCreateGroup}>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Group Name *
                </label>
                <input
                  type="text"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  required
                  minLength={3}
                  maxLength={200}
                  placeholder="e.g., Seattle ADHD Support Group"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Description
                </label>
                <textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  placeholder="What is this group about?"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={newGroup.locationCity}
                    onChange={(e) => setNewGroup({ ...newGroup, locationCity: e.target.value })}
                    placeholder="e.g., Seattle"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={newGroup.locationState}
                    onChange={(e) => setNewGroup({ ...newGroup, locationState: e.target.value })}
                    placeholder="e.g., WA"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Identity *
                </label>
                <select
                  value={newGroup.targetIdentityId}
                  onChange={(e) => setNewGroup({ ...newGroup, targetIdentityId: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                >
                  {userIdentities.length === 0 ? (
                    <option value="">No identities found — add one in your Profile first</option>
                  ) : (
                    userIdentities.map(identity => (
                      <option 
                        key={identity.identity_id} 
                        value={identity.identity_id}
                      >
                        {identity.category_icon} {identity.category_display_name}: {identity.identity_name}
                      </option>
                    ))
                  )}
                </select>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {userIdentities.length === 0 
                    ? <span style={{ color: '#dc2626' }}>Go to <a href="/profile">your profile</a> to add an identity first</span>
                    : 'Only members with this identity will see this group'
                  }
                </div>
              </div>

              <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                >
                  Nah, go back
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Groups;

